import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProducts, imgUrl } from "@/lib/api";
import { MOTIF_LIST, Motif as MotifIcon } from "@/lib/motifs";
import { lifestyleFor } from "@/data/lifestyle";
import MotifCompanions from "@/components/product/MotifCompanions";
import SeoHead from "@/components/site/SeoHead";

const Motif = () => {
  const { motif } = useParams();
  const [items, setItems] = useState([]);
  const label = (MOTIF_LIST.find((m) => m.key === motif) || {}).label || motif;

  useEffect(() => {
    fetchProducts({ motif }).then(setItems);
  }, [motif]);

  return (
    <div className="pt-32 md:pt-40 pb-32 relative overflow-hidden" data-testid={`motif-page-${motif}`}>
      <SeoHead
        title={`${label} · shop by motif`}
        description={`MELAVI pieces built around the ${label.toLowerCase()} motif — hand-embroidered by our atelier in Bengaluru.`}
        path={`/motif/${motif}`}
      />
      {/* Companion flock — sits BEHIND the content */}
      <div className="absolute left-0 right-0 top-24 h-[520px] md:h-[620px] pointer-events-none" style={{ zIndex: 0 }}>
        <MotifCompanions motif={motif} />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 relative" style={{ zIndex: 1 }}>
        <div className="grid md:grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-16 items-end">
          <div className="md:col-span-8">
            <div className="nav-item mb-3 md:mb-4">Shop by motif</div>
            <h1 className="serif text-4xl md:text-7xl leading-[1.02]">
              The <em className="italic">{label.toLowerCase()}</em>.
            </h1>
          </div>
          <div className="md:col-span-2 md:col-start-11 text-accent hidden md:block">
            <MotifIcon name={motif} size={80} strokeColor="var(--accent)" />
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-ink/60">No pieces on this motif yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-14">
            {items.map((p, i) => {
              const skew = ["-rotate-[1.2deg]", "rotate-[0.8deg]", "-rotate-[0.6deg]"][i % 3];
              return (
                <Link
                  key={p.slug}
                  to={`/product/${p.slug}`}
                  className={`group relative block ${skew} transition-transform hover:rotate-0 hover:-translate-y-1 duration-500`}
                  data-testid={`motif-tile-${p.slug}`}
                >
                  {/* kraft-paper specimen card */}
                  <div
                    className="p-3 md:p-4"
                    style={{
                      background: "#FBF5E6",
                      boxShadow:
                        "0 20px 30px -14px rgba(28,26,25,0.28), 0 2px 4px rgba(28,26,25,0.08)",
                    }}
                  >
                    {/* red-pin corners */}
                    <span
                      className="absolute -top-1 left-4 w-2.5 h-2.5 rounded-full"
                      style={{ background: "#AE2B31", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                      aria-hidden
                    />
                    <span
                      className="absolute -top-1 right-4 w-2.5 h-2.5 rounded-full"
                      style={{ background: "#AE2B31", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                      aria-hidden
                    />
                    <div className="overflow-hidden">
                      <img
                        src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                        alt={p.name}
                        loading="lazy"
                        className="w-full aspect-[3/4] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-ink/50">
                        Nº {String(i + 1).padStart(2, "0")}
                      </span>
                      <MotifIcon name={motif} size={16} strokeColor="var(--accent)" />
                    </div>
                    <div
                      className="serif text-lg md:text-xl mt-1 leading-tight italic"
                      style={{ color: "var(--ink)" }}
                    >
                      {p.name}
                    </div>
                    <div className="text-xs text-ink/60 mt-0.5">
                      {p.category === "women"
                        ? "Adults · women"
                        : p.category === "men"
                          ? "Teens"
                          : `Little ${p.category}`}
                      {" · "}₹{p.price.toLocaleString("en-IN")}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Motif;
